import { useEffect, useState } from 'react';
import { settingsService } from './services/api';

/**
 * LevNutri — Branding white-label (paletas + aplicação de tema).
 *
 * As chaves das paletas devem bater com o backend (branding-helper.php e o
 * whitelist em settings.php). A aplicação é feita via CSS variables do
 * index.css — componentes que ainda usam cores hardcoded migram aos poucos.
 */

// bg / bg2: fundo da área de trabalho (gradiente bem claro, tom da paleta).
export const PALETAS = {
  terra: { label: 'Terra (padrão)', primary: '#D4A574', primaryLight: '#E8C9A8', primaryDark: '#B8895C', dark: '#5a3e2b', strong: '#9a6a42', bg: '#fbf8f5', bg2: '#f6f0ea' },
  verde: { label: 'Verde',          primary: '#6f9a77', primaryLight: '#b9d2bd', primaryDark: '#46714f', dark: '#2f5236', strong: '#46714f', bg: '#f5f9f6', bg2: '#e8f1ea' },
  rosa:  { label: 'Rosa',           primary: '#e3b3ad', primaryLight: '#e3b3ad', primaryDark: '#c46a60', dark: '#7a2e27', strong: '#a8483f', bg: '#fcf6f5', bg2: '#f8e9e7' },
  azul:  { label: 'Azul',           primary: '#7189a6', primaryLight: '#b8c7d8', primaryDark: '#7189a6', dark: '#34495e', strong: '#4f6a86', bg: '#f5f8fb', bg2: '#e8eef5' },
  roxo:  { label: 'Roxo',           primary: '#A98FD1', primaryLight: '#d6cbea', primaryDark: '#7c62a8', dark: '#4a3570', strong: '#6b4f99', bg: '#f8f6fc', bg2: '#eee9f7' },
};

export function aplicarPaleta(key) {
  const p = PALETAS[key] || PALETAS.terra;
  const r = document.documentElement.style;
  r.setProperty('--color-primary', p.primary);
  r.setProperty('--color-primary-light', p.primaryLight);
  r.setProperty('--color-primary-dark', p.primaryDark);
  // Tom escuro para botões e links: contraste ≥ 4.5 com texto branco
  r.setProperty('--color-primary-strong', p.strong);
  // --color-accent é usado em gradientes/realces no CSS global (sidebar, botões);
  // sem isto a paleta trocava só o tom base. Aponta para a variante clara.
  r.setProperty('--color-accent', p.primaryLight);
  // Fundo da tela acompanha a paleta (o modo escuro tem fundo próprio).
  r.setProperty('--bg', p.bg);
  r.setProperty('--app-bg', p.bg);
  r.setProperty('--app-bg-2', p.bg2);
}

/** URL absoluta da logo a partir do logo_path do backend ('/uploads/branding/...'). */
export function logoUrl(logoPath) {
  if (!logoPath) return '';
  const base = process.env.REACT_APP_API_URL || 'https://app.levnutri.com/api';
  return base + logoPath;
}

/** Logo padrão do produto, usada quando a nutricionista não enviou a dela. */
export const LOGO_LEVNUTRI = 'https://www.levnutri.com/assets/logo.png';

export function logoScale(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 50 && n <= 300 ? n : 100;
}
export function reportLogoStyle(marca, baseHeight) {
  return { height: `${baseHeight * logoScale(marca?.reportScale) / 100}pt`, maxWidth: '100%', objectFit: 'contain' };
}

// Marca da nutricionista logada, compartilhada entre telas e documentos impressos.
let marcaCache = null;
let marcaToken = null;
const inscritos = new Set();
const marcaVazia = () => ({ logo: '', businessName: '', menuScale: 100, reportScale: 100 });
function publicarMarca(m, token = localStorage.getItem('token')) {
  if (token !== localStorage.getItem('token')) return;
  marcaToken = token;
  marcaCache = m;
  inscritos.forEach(fn => fn(m));
}
function marcaDe(d = {}) {
  return {
    logo: d.logo_path ? logoUrl(d.logo_path) : '',
    businessName: (d.business_name || '').trim(),
    menuScale: logoScale(d.logo_menu_scale),
    reportScale: logoScale(d.logo_report_scale),
  };
}

/** Marca da conta atual; respostas de uma sessão anterior são descartadas. */
export function useMarca() {
  const token = localStorage.getItem('token');
  const [marca, setMarca] = useState(() =>
    marcaToken === token && marcaCache ? marcaCache : marcaVazia());
  useEffect(() => {
    inscritos.add(setMarca);
    if (marcaToken !== token) {
      marcaToken = token;
      marcaCache = null;
    }
    setMarca(marcaCache || marcaVazia());
    if (!marcaCache && token) {
      settingsService.get().then(r => {
        if (r.success && r.data) publicarMarca(marcaDe(r.data), token);
      }).catch(() => {});
    }
    return () => { inscritos.delete(setMarca); };
  }, [token]);
  return marca;
}

/** Avisa o App (sidebar/header) que a marca mudou sem precisar de reload. */
export function notificarBranding(detail) {
  publicarMarca({
    logo: detail?.logo_path ? logoUrl(detail.logo_path) : '',
    businessName: (detail?.business_name ?? marcaCache?.businessName ?? '').trim(),
    menuScale: logoScale(detail?.logo_menu_scale ?? marcaCache?.menuScale),
    reportScale: logoScale(detail?.logo_report_scale ?? marcaCache?.reportScale),
  });
  window.dispatchEvent(new CustomEvent('levnutri:branding', { detail }));
}
